import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../lib/AuthContext'
import { useLang } from '../lib/LangContext'
import { getMouvements, getStatsJour } from '../lib/api'

const CAT_EMOJI = { alimentation: '🍚', boissons: '🥤', hygiene: '🧴', epices: '🌶', conserves: '🥫', autres: '📦' }

function timeAgo(dateStr, lang) {
  const diff = Date.now() - new Date(dateStr).getTime()
  if (diff < 60000) return lang === 'fr' ? 'À l\'instant' : 'Just now'
  if (diff < 3600000) return Math.floor(diff / 60000) + (lang === 'fr' ? ' min' : ' min')
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h'
  return Math.floor(diff / 86400000) + 'j'
}

export default function Dashboard({ produits, produitsLoading }) {
  const { user, boutique, boutiqueId } = useAuth()
  const { t, lang } = useLang()
  const [mouvements, setMouvements] = useState([])
  const [nbMouvementsJour, setNbMouvementsJour] = useState(0)
  const [encaisseJour, setEncaisseJour] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!boutiqueId) return
    setLoading(true)
    const [{ data: movs, error: errMov }, { data: stats, error: errStats }] = await Promise.all([
      getMouvements(boutiqueId, 5),
      getStatsJour(boutiqueId)
    ])
    if (errMov || errStats) {
      setError(lang === 'fr' ? 'Impossible de charger le tableau de bord.' : "Couldn't load the dashboard.")
    } else {
      setError(null)
      setMouvements(movs || [])
      setNbMouvementsJour(stats?.nbMouvements || 0)
      setEncaisseJour(stats?.encaisse || 0)
    }
    setLoading(false)
    setLoaded(true)
  }, [boutiqueId, lang])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', load)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', load)
    }
  }, [load])

  const lowProds = produits.filter(p => p.quantite > 0 && p.quantite <= p.seuil_alerte)
  const outProds = produits.filter(p => p.quantite === 0)
  const alertProds = [...outProds, ...lowProds]

  const greeting = () => {
    const h = new Date().getHours()
    if (h < 12) return lang === 'fr' ? 'Bonjour' : 'Good morning'
    if (h < 18) return lang === 'fr' ? 'Bon après-midi' : 'Good afternoon'
    return lang === 'fr' ? 'Bonsoir' : 'Good evening'
  }

  const produitsPending = !!produitsLoading
  const statsPending = loading && !loaded

  return (
    <div className="main">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div className="greeting" style={{ marginBottom: 0 }}>
          <div className="greeting-time">{new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
          <div className="greeting-name">{greeting()}, {boutique?.nom?.split(' ')[0] || '—'} 👋</div>
        </div>
        <button onClick={load} disabled={loading} style={{
          background: 'var(--sage-pale)', border: '1px solid var(--sage-light)', borderRadius: 50,
          padding: '5px 14px', cursor: 'pointer', fontSize: 12, color: 'var(--sage-dark)',
          fontFamily: 'DM Sans, sans-serif', fontWeight: 500, flexShrink: 0
        }}>{loading ? '⏳' : '🔄'}</button>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* ENCAISSE DU JOUR — mis en avant au-dessus des stats */}
      <div className="stat-card accent-sage stat-hero">
        <div className="stat-hero-label">💰 {lang === 'fr' ? "Encaissé aujourd'hui" : 'Received today'}</div>
        <div className="stat-hero-value">{statsPending ? '…' : `${encaisseJour.toLocaleString()} FCFA`}</div>
      </div>

      <div className="stats-grid">
        <div className="stat-card accent-sage">
          <span className="stat-icon">📦</span>
          <div className="stat-value">{produitsPending ? '…' : produits.length}</div>
          <div className="stat-label">{t('products')}</div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">🔄</span>
          <div className="stat-value">{statsPending ? '…' : nbMouvementsJour}</div>
          <div className="stat-label">{t('movements_today')}</div>
        </div>
        <div className="stat-card accent-amber">
          <span className="stat-icon">⚠️</span>
          <div className="stat-value">{produitsPending ? '…' : lowProds.length}</div>
          <div className="stat-label">{t('low_stock')}</div>
        </div>
        <div className="stat-card accent-rose">
          <span className="stat-icon">🚨</span>
          <div className="stat-value">{produitsPending ? '…' : outProds.length}</div>
          <div className="stat-label">{t('out_of_stock')}</div>
        </div>
      </div>

      {/* ALERTES STOCK */}
      {!produitsPending && alertProds.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div className="section-label">{t('alerts')}</div>
          {alertProds.slice(0, 3).map(p => (
            <div key={p.id} className={`list-row ${p.quantite === 0 ? 'list-row-out' : 'list-row-low'}`}>
              <span className="list-row-icon">{CAT_EMOJI[p.categorie] || '📦'}</span>
              <div className="list-row-body">
                <div className="list-row-title">{p.nom}</div>
                <div className="list-row-sub">
                  {p.quantite === 0
                    ? (lang === 'fr' ? 'Rupture de stock' : 'Out of stock')
                    : `${p.quantite} ${p.unite} ${lang === 'fr' ? 'restant(s)' : 'remaining'}`}
                </div>
              </div>
              <div className="list-row-meta" style={{ color: p.quantite === 0 ? 'var(--rose)' : 'var(--amber)' }}>
                {p.quantite === 0 ? '🚨' : '⚠️'}
              </div>
            </div>
          ))}
          {alertProds.length > 3 && (
            <div className="list-more">
              +{alertProds.length - 3} {lang === 'fr' ? 'autres alertes' : 'more alerts'}
            </div>
          )}
        </div>
      )}

      {/* ACTIVITÉ RÉCENTE */}
      <div className="section-label">{t('recent_activity')}</div>
      {statsPending ? (
        <div className="empty">{t('loading')}</div>
      ) : mouvements.length === 0 ? (
        <div className="empty">
          <span className="empty-icon">📋</span>
          {t('no_movements')}
        </div>
      ) : (
        mouvements.map(m => (
          <div key={m.id} className="list-row">
            <span className="list-row-icon">{m.type === 'entree' ? '📥' : '📤'}</span>
            <div className="list-row-body">
              <div className="list-row-title">{m.produit_nom}</div>
              <div className="list-row-sub">
                {m.type === 'entree' ? (lang === 'fr' ? 'Entrée' : 'Entry') : (lang === 'fr' ? 'Sortie' : 'Exit')} · {m.quantite} {m.produits?.unite || ''}
              </div>
            </div>
            <div className="list-row-meta muted">{timeAgo(m.created_at, lang)}</div>
          </div>
        ))
      )}
    </div>
  )
}
